import { DatabaseSelection } from '@blocksuite/affine/blocks/database';
import { DATABASE_LARGE_DATA_POLICY } from '@streamlain/database-identity';

import type { DatabaseAttachment } from '../components/ai-chat-content/type';
import { applyDatabaseRowSelectionScope } from './database-row-selection-scope';

function databaseAttachmentSpecificity(attachment: DatabaseAttachment): number {
  if (attachment.selectedRowIds?.length) return 3;
  if (attachment.visibleRowWindow?.rowIds.length) return 2;
  return 1;
}

function normalizeDatabaseAttachment(
  attachment: DatabaseAttachment
): DatabaseAttachment {
  if (attachment.selectedRowIds?.length) {
    const selectedRowIds = attachment.selectedRowIds.slice(
      0,
      DATABASE_LARGE_DATA_POLICY.ai.contextSelectedRowLimit
    );
    return {
      ...attachment,
      scope: 'selected-rows',
      selectedRowIds,
      visibleRowWindow: undefined,
    };
  }

  if (!attachment.visibleRowWindow) return attachment;

  const rowIds = attachment.visibleRowWindow.rowIds.slice(
    0,
    DATABASE_LARGE_DATA_POLICY.ai.contextRowWindowLimit
  );
  return {
    ...attachment,
    scope: 'visible-window',
    visibleRowWindow: {
      ...attachment.visibleRowWindow,
      limit: Math.min(
        attachment.visibleRowWindow.limit,
        DATABASE_LARGE_DATA_POLICY.ai.contextRowWindowLimit
      ),
      rowIds,
      hasMore:
        attachment.visibleRowWindow.offset + rowIds.length <
        attachment.visibleRowWindow.totalCount,
    },
  };
}

/**
 * Keep exactly one AI context attachment per database.
 *
 * Row selections are the user's most specific intent and replace the
 * database's visible-page context. Without a selection, the current bounded
 * page remains the maximum implicit context; the whole database is never
 * promoted implicitly.
 */
export function normalizeDatabaseContextAttachments(
  attachments: readonly DatabaseAttachment[]
): DatabaseAttachment[] {
  const normalizedByDatabase = new Map<string, DatabaseAttachment>();

  for (const rawAttachment of attachments) {
    const attachment = normalizeDatabaseAttachment(rawAttachment);
    // Block IDs are workspace-global. Some selection paths omit docId, so
    // including it would let the same database appear twice.
    const key = attachment.blockId;
    const current = normalizedByDatabase.get(key);
    if (
      !current ||
      databaseAttachmentSpecificity(attachment) >
        databaseAttachmentSpecificity(current)
    ) {
      normalizedByDatabase.set(key, attachment);
    }
  }

  return [...normalizedByDatabase.values()];
}

export function applyDatabaseRowSelectionContext(
  attachment: DatabaseAttachment,
  selection: unknown
): DatabaseAttachment {
  if (!(selection instanceof DatabaseSelection)) {
    return attachment;
  }
  return applyDatabaseRowSelectionScope(attachment, selection.viewSelection);
}
